<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15">
      <h2 class="fs-4 text-black fw-semibold mb-10 text-center">Continuer la session</h2>
     
      <div v-if="isLoading" class="text-center">
        <p class="mb-10">Chargement des questions...</p>
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
      </div>
      <!-- Message si le test est déjà terminé -->
      <div v-else-if="isTestCompleted" class="text-center">
        <div class="alert alert-warning">
          <h4 class="mb-10">⚠️ Test déjà terminé</h4>
          <p class="mb-20">
            Ce test est déjà terminé, impossible de le continuer. Toutes les questions ont déjà été répondues.
          </p>
          <div class="d-flex justify-content-center gap-2">
            <router-link to="/liste-sessions" class="btn btn-secondary">
              <i class="fa fa-arrow-left me-1"></i>
              Retour à la liste
            </router-link>
            <router-link
              :to="{ name: 'ViewSessionPage', params: { id: sessionId } }"
              class="btn btn-info"
              v-if="sessionId"
            >
              <i class="fa fa-eye me-1"></i>
              Voir les résultats
            </router-link>
          </div>
        </div>
      </div>

      <div v-else-if="showResults" class="results-container">
        <div class="">
          <center>
            <h3 class="mb-20">Test Terminé !</h3>
            <p v-if="sessionId">Session ID: {{ sessionId }}</p>
            <p v-if="sessionCode">Code de la session: {{ sessionCode }}</p>
          </center>
          <div class="row gx-2">
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Score</h4>
                <p class="fs-1 fw-bold text-success">{{ testResults.score }} / {{ testResults.total }}</p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Pourcentage</h4>
                <p class="fs-1 fw-bold" :class="testResults.percentage >= 50 ? 'text-success' : 'text-danger'">
                  {{ testResults.percentage }}%
                </p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Bonnes réponses</h4>
                <p class="fs-1 fw-bold text-success">{{ testResults.correctAnswers }}</p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Mauvaises réponses</h4>
                <p class="fs-1 fw-bold text-danger">{{ testResults.incorrectAnswers }}</p>
              </div>
            </div>
          </div>
         
          <div class="text-start mt-10">
            <h4 class="mb-15 text-center">📋 Revue détaillée de vos réponses</h4>
            <div class="questions-review">
              <div 
                v-for="(question, index) in questionsWithAnswers" 
                :key="question.id"
                class="question-review-item mb-15 border"
                :class="question.isCorrect ? 'border-success' : 'border-danger'"
              >
                <div class="question-header p-5" :class="question.isCorrect ? 'bg-success-subtle' : 'bg-danger-subtle'">
                  <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0 fw-bold">
                      <span class="question-number">Question {{ index + 1 }}</span>
                    </h5>
                    <span class="fs-3" :class="question.isCorrect ? 'text-success' : 'text-danger'">
                      {{ question.isCorrect ? '✓' : '✗' }}
                    </span>
                  </div>
                </div>
                
                <div class="question-content p-10 bg-white">
                  <p class="fw-semibold mb-10 text-black">{{ question.contenu }}</p>
                  
                  <div class="user-answer mb-5 p-10 rounded" :class="question.isCorrect ? 'bg-light' : 'bg-light'">
                    <strong class="text-black">Votre réponse :</strong>
                    <span 
                      class="ms-2 fw-semibold"
                      :class="question.isCorrect ? 'text-success' : 'text-danger text-decoration-line-through'"
                    >
                      {{ question.userAnswerText }}
                    </span>
                  </div>
                  
                  <div v-if="!question.isCorrect" class="correct-answer-display p-10 bg-success-subtle rounded">
                    <strong class="text-success">✓ Bonne réponse :</strong>
                    <span class="ms-2 text-success fw-bold">
                      {{ question.correctAnswerText }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
         
        </div>
      </div>
      <Form
        v-else-if="questions.length > 0"
        ref="testForm"
        @submit="handleSubmit"
        :validation-schema="testSchema"
      >
        <div class="progress mb-20" style="height: 8px;">
          <div 
            class="progress-bar bg-success" 
            :style="{ width: progressPercentage + '%' }"
          ></div>
        </div>
        <div v-if="currentQuestion" class="form-group mb-10">
          <label class="d-block text-black fw-semibold mb-10">
            Question {{ currentQuestionIndex + 1 }} / {{ questions.length }}
          </label>
          <div class="alert alert-info">
            <strong>{{ currentQuestion.contenu }}</strong>
          </div>
        </div>
        <div
          v-if="currentQuestion && currentQuestion.type === 'choix_multiple'"
          class="form-group mb-10"
        >
          <label class="d-block text-black fw-semibold mb-10">
            Sélectionner une réponse <span class="text-danger">*</span>
          </label>
          <div v-if="propositionOptions.length > 0" class="border border-primary p-10">
            <div v-for="(proposition, index) in propositionOptions" :key="`prop-${proposition.value}-${index}`">
              <div class="d-flex align-items-center mb-5">
                <Field
                  name="answer"
                  type="radio"
                  :value="proposition.value"
                  v-model="formData.answer"
                  :id="`proposition-${proposition.value}`"
                  class="form-check-input me-2"
                  @change="onAnswerChange"
                />
                <label
                  class="text-black"
                  :for="`proposition-${proposition.value}`"
                  style="cursor: pointer;"
                >
                  {{ proposition.label }}
                </label>
              </div>
            </div>
            <ErrorMessage name="answer" class="text-danger" />
          </div>
          <div v-else class="text-danger">
            Aucune proposition disponible pour cette question.
          </div>
        </div>
        <div v-else-if="currentQuestion && currentQuestion.type === 'texte_libre'" class="form-group mb-10">
          <label class="d-block text-black fw-semibold mb-10">
            Votre réponse <span class="text-danger">*</span>
          </label>
          <Field
            name="answer"
            as="textarea"
            class="form-control shadow-none text-black"
            v-model="formData.answer"
            placeholder="Entrez votre réponse"
            rows="4"
            @input="onAnswerChange"
          />
          <ErrorMessage name="answer" class="text-danger" />
        </div>
        <div v-else-if="currentQuestion && currentQuestion.type === 'oui_non'" class="form-group mb-10">
          <label class="d-block text-black fw-semibold mb-10">
            Sélectionner une réponse <span class="text-danger">*</span>
          </label>
          <div class="border border-primary p-10">
            <div class="d-flex align-items-center mb-5">
              <Field
                name="answer"
                type="radio"
                value="Oui"
                v-model="formData.answer"
                id="oui"
                class="form-check-input me-2"
                @change="onAnswerChange"
              />
              <label class="text-black" for="oui" style="cursor: pointer;">Oui</label>
            </div>
            <div class="d-flex align-items-center mb-5">
              <Field
                name="answer"
                type="radio"
                value="Non"
                v-model="formData.answer"
                id="non"
                class="form-check-input me-2"
                @change="onAnswerChange"
              />
              <label class="text-black" for="non" style="cursor: pointer;">Non</label>
            </div>
            <ErrorMessage name="answer" class="text-danger" />
          </div>
        </div>
                <!-- Actions (boutons) -->
        <div class="d-flex align-items-center mt-10">
          <button
            type="button"
            class="btn btn-secondary me-3"
            @click="previousQuestion"
            :disabled="currentQuestionIndex === 0"
            :class="{ 'cursor-not-allowed': currentQuestionIndex === 0 }"
          >
            Précédent
          </button>

          <button
            type="submit"
            class="btn"
            :class="[
              isLastQuestion ? 'btn-success' : 'btn-info',
              { 'cursor-not-allowed': isDisable }
            ]"
            :disabled="isDisable || isSubmitting"
          >
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else :class="isLastQuestion ? 'fa fa-check me-1' : 'fa fa-arrow-right me-1'"></i>
            {{ isLastQuestion ? 'Terminer le test' : 'Suivant' }}
          </button>
        </div>
        <div class="mt-10" v-if="isDisable">
          <small class="text-danger">
            
            <span v-if="!currentQuestion">Aucune question disponible</span>
            
          </small>
        </div>
        <div class="mt-10" v-else>
          <small class="text-success">
            <i class="fa fa-check-circle me-1"></i>
            Prêt à continuer
          </small>
        </div>
      </Form>
      <div v-else class="text-danger text-center">
        <div class="alert alert-danger">
          <h4>Test impossible à continuer</h4>
          <p class="mb-10">
            Aucune question n'est associée à cette session.
          </p>
          <div class="d-flex justify-content-center gap-2">
            <router-link to="/liste-sessions" class="btn btn-primary">
              <i class="fa fa-arrow-left me-1"></i>
              Retour
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted, watch } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import { useRouter, useRoute } from 'vue-router';
import { success, error, format_date } from '@/utils/utils';
import ApiService from '@/services/ApiService';

interface Proposition {
  id: number;
  contenu: string;
  estLaBonneReponse?: boolean;
}

interface Question {
  id: number;
  contenu: string;
  type: string;
  propositions?: Proposition[];
}

interface FormValues {
  answer: string | null;
}

interface TestResults {
  score: number;
  total: number;
  percentage: number;
  correctAnswers: number;
  incorrectAnswers: number;
}

interface QuestionWithAnswer {
  id: number;
  contenu: string;
  type: string;
  userAnswerText: string;
  correctAnswerText: string;
  isCorrect: boolean;
}

export default defineComponent({
  name: 'ContinuerSessionPage',
  components: {
    Form,
    Field,
    ErrorMessage,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const testForm = ref<InstanceType<typeof Form> | null>(null);
    const questions = ref<Question[]>([]);
    const currentQuestionIndex = ref(0);
    const isLoading = ref(true);
    const isSubmitting = ref(false);
    const sessionId = ref<number | null>(null);
    const sessionCode = ref<string | null>(null); // Ajout de sessionCode
    const showResults = ref(false);
    const isTestCompleted = ref(false);
    const testResults = reactive<TestResults>({
      score: 0,
      total: 0,
      percentage: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
    });
    const formData = reactive<FormValues>({
      answer: null,
    });
    const answers = ref<{ [key: number]: string }>({});

    const testSchema = Yup.object().shape({
      answer: Yup.string().required('Une réponse est obligatoire'),
    });

    const currentQuestion = computed(() => {
      return questions.value[currentQuestionIndex.value] || null;
    });

    const propositionOptions = computed(() => {
      if (currentQuestion.value && currentQuestion.value.type === 'choix_multiple') {
        console.log('Propositions brutes:', currentQuestion.value.propositions);
        const options = currentQuestion.value.propositions?.map((prop) => ({
          value: String(prop.id),
          label: prop.contenu || 'Proposition inconnue',
        })) || [];
        console.log('PropositionOptions:', options);
        return options;
      }
      return [];
    });

    const questionsWithAnswers = computed((): QuestionWithAnswer[] => {
      return questions.value.map((question) => {
        const userAnswer = answers.value[question.id];
        let userAnswerText = 'Non répondu';
        let correctAnswerText = 'Non disponible';
        let isCorrect = false;

        // Trouver la bonne réponse
        const correctProposition = question.propositions?.find(p => p.estLaBonneReponse);
        if (correctProposition) {
          correctAnswerText = correctProposition.contenu;
        }

        // Traiter la réponse de l'utilisateur
        if (userAnswer && userAnswer.trim() !== '') {
          if (question.type === 'choix_multiple' || question.type === 'oui_non') {
            const userProposition = question.propositions?.find(p => String(p.id) === userAnswer);
            userAnswerText = userProposition ? userProposition.contenu : userAnswer;
            isCorrect = userProposition?.estLaBonneReponse || false;
          } else if (question.type === 'texte_libre') {
            userAnswerText = userAnswer;
            isCorrect = correctProposition ? userAnswer.toLowerCase().trim() === correctProposition.contenu.toLowerCase().trim() : false;
          }
        }

        return {
          id: question.id,
          contenu: question.contenu,
          type: question.type,
          userAnswerText,
          correctAnswerText,
          isCorrect,
        };
      });
    });

    const isLastQuestion = computed(() => {
      return currentQuestionIndex.value === questions.value.length - 1;
    });

    const isDisable = computed(() => {
      return !currentQuestion.value || !formData.answer || formData.answer.trim() === '';
    });

    const progressPercentage = computed(() => {
      if (questions.value.length === 0) return 0;
      return Math.round(((currentQuestionIndex.value + 1) / questions.value.length) * 100);
    });

    const answeredCount = computed(() => {
      return Object.keys(answers.value).length;
    });

    const resetState = () => {
      questions.value = [];
      answers.value = {};
      currentQuestionIndex.value = 0;
      formData.answer = null;
      showResults.value = false;
      isTestCompleted.value = false;
      sessionCode.value = null; // Réinitialiser sessionCode
      Object.assign(testResults, {
        score: 0,
        total: 0,
        percentage: 0,
        correctAnswers: 0,
        incorrectAnswers: 0,
      });
    };

    const loadSessionQuestions = async () => {
      try {
        isLoading.value = true;
        resetState();
       
        sessionId.value = Number(route.params.id);
        console.log('Chargement des questions pour la session:', sessionId.value);

        // Récupérer les informations de la session (y compris le code)
        const sessionResponse = await ApiService.get(`/sessions/${sessionId.value}`);
        if (sessionResponse.data.code === 200 && sessionResponse.data.data) {
          sessionCode.value = sessionResponse.data.data.code || null;
          console.log('Code de la session:', sessionCode.value);
        } else {
          throw new Error('Impossible de récupérer les informations de la session');
        }

        // Récupérer les questions
        const questionsResponse = await ApiService.get(`/sessions/${sessionId.value}/questions`);
        if (questionsResponse.data.code === 200 && questionsResponse.data.data) {
          questions.value = questionsResponse.data.data;
          console.log('Questions chargées:', questions.value);
         
          // Récupérer les réponses préexistantes
          const reponsesResponse = await ApiService.get(`/sessions/${sessionId.value}/reponses`);
          if (reponsesResponse.data.code === 200 && reponsesResponse.data.data.reponses) {
            answers.value = {};
            reponsesResponse.data.data.reponses.forEach((reponse: any) => {
              console.log('Réponse brute:', reponse);
              const questionId = reponse.question?.id;
              if (questionId) {
                const answerValue = reponse.proposition?.id ? String(reponse.proposition.id) : (reponse.contenu || '');
                if (answerValue && answerValue.trim() !== '') {
                  answers.value[questionId] = answerValue;
                }
              }
            });
            console.log('Réponses préexistantes:', answers.value);
          }
         
          // Vérifier si toutes les questions ont une réponse valide
          const allQuestionsAnswered = questions.value.every(q => {
            const answer = answers.value[q.id];
            return answer !== undefined && answer !== null && answer.trim() !== '';
          });
         
          if (allQuestionsAnswered && questions.value.length > 0) {
            console.log('Test déjà terminé - Toutes les questions ont été répondues');
            isTestCompleted.value = true;
            await fetchTestResults();
            isLoading.value = false;
            return;
          }
         
          // Trouver la première question non répondue
          const firstUnansweredIndex = questions.value.findIndex(q => {
            const answer = answers.value[q.id];
            return answer === undefined || answer === null || answer.trim() === '';
          });
          if (firstUnansweredIndex >= 0) {
            currentQuestionIndex.value = firstUnansweredIndex;
            formData.answer = null;
          } else {
            currentQuestionIndex.value = questions.value.length - 1;
            const lastQuestion = questions.value[currentQuestionIndex.value];
            formData.answer = answers.value[lastQuestion?.id || 0] || null;
          }
         
          console.log('Index de la première question non répondue:', currentQuestionIndex.value);
         
          if (testForm.value) {
            testForm.value.resetForm({ values: { answer: formData.answer } });
          }
          onAnswerChange();
        } else {
          throw new Error('Aucune question trouvée pour cette session');
        }
      } catch (err: any) {
        console.error('Erreur lors du chargement des questions:', err);
        error(err.response?.data?.message || 'Erreur lors du chargement des questions');
      } finally {
        isLoading.value = false;
      }
    };

    const fetchTestResults = async () => {
      if (!sessionId.value) {
        console.error('Session ID manquant, impossible de récupérer les résultats');
        return;
      }
      try {
        console.log('Récupération des résultats pour la session:', sessionId.value);
        const { data } = await ApiService.get(`/sessions/${sessionId.value}/results`);
       
        console.log('Résultats reçus:', data);
       
        if (data && data.code === 200 && data.data) {
          testResults.score = data.data.score || 0;
          testResults.total = data.data.total || questions.value.length;
          testResults.percentage = data.data.percentage || 0;
          testResults.correctAnswers = data.data.correctAnswers || 0;
          testResults.incorrectAnswers = data.data.incorrectAnswers || 0;
         
          console.log('Résultats du test:', testResults);
        } else {
          throw new Error('Format de réponse invalide pour les résultats');
        }
      } catch (err: any) {
        console.error('Erreur lors de la récupération des résultats:', err);
        error(`Impossible de récupérer les résultats: ${err.message || 'Erreur inconnue'}`);
      }
    };

    const saveTestResults = async () => {
      if (!sessionId.value) {
        console.error('Session ID manquant, impossible d\'enregistrer les résultats');
        error('Session non trouvée. Veuillez recommencer le test.');
        return false;
      }
      try {
        const submissionData = {
          score: testResults.score,
          total: testResults.total,
          percentage: testResults.percentage,
          correctAnswers: testResults.correctAnswers,
          incorrectAnswers: testResults.incorrectAnswers,
          answers: Object.entries(answers.value).map(([questionId, answer]) => {
            const question = questions.value.find(q => q.id === Number(questionId));
            if (!question) return null;
            if (question.type === 'choix_multiple') {
              return {
                questionId: Number(questionId),
                propositionId: Number(answer),
                contenu: null,
              };
            } else if (question.type === 'oui_non') {
              const proposition = question.propositions?.find(p => p.contenu === answer);
              return {
                questionId: Number(questionId),
                propositionId: proposition ? proposition.id : null,
                contenu: answer,
              };
            } else {
              return {
                questionId: Number(questionId),
                propositionId: null,
                contenu: answer,
              };
            }
          }).filter(answer => answer !== null),
        };
        console.log('Envoi des résultats à l\'API:', submissionData);
        const response = await ApiService.post(`/sessions/${sessionId.value}/results`, submissionData);
        console.log('Réponse API enregistrement des résultats:', response);
        if (response.data && (response.data.code === 201 || response.data.code === 200)) {
          console.log('✓ Résultats enregistrés avec succès');
          return true;
        } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'enregistrement des résultats');
        }
      } catch (err: any) {
        console.error('Erreur lors de l\'enregistrement des résultats:', err);
        let errorMessage = 'Erreur lors de l\'enregistrement des résultats. Veuillez réessayer.';
        if (err.response?.status === 404) {
          errorMessage = 'L\'endpoint pour enregistrer les résultats est introuvable. Vérifiez la configuration du serveur.';
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        }
        error(errorMessage);
        return false;
      }
    };

    const onAnswerChange = () => {
      console.log('Réponse sélectionnée:', formData.answer);
      if (testForm.value) {
        testForm.value.setFieldValue('answer', formData.answer);
        testForm.value.validateField('answer');
      }
    };

    const saveAnswer = async (questionId: number, answer: string) => {
      if (!sessionId.value) {
        console.error('Session ID manquant, impossible d\'enregistrer la réponse');
        return false;
      }
      const question = questions.value.find(q => q.id === questionId);
      if (!question) {
        console.error('Question non trouvée');
        return false;
      }
      console.log(`Enregistrement de la réponse pour la question ${questionId}:`, { type: question.type, answer });
      let formattedAnswer;
      if (question.type === 'choix_multiple') {
        formattedAnswer = {
          questionId: questionId,
          propositionId: Number(answer),
          contenu: null,
        };
      } else if (question.type === 'oui_non') {
        const proposition = question.propositions?.find(p => p.contenu === answer);
        formattedAnswer = {
          questionId: questionId,
          propositionId: proposition ? proposition.id : null,
          contenu: answer,
        };
      } else {
        formattedAnswer = {
          questionId: questionId,
          propositionId: null,
          contenu: answer,
        };
      }
      try {
        const submissionData = {
          sessionId: sessionId.value,
          answers: [formattedAnswer],
        };
        console.log('Envoi de la réponse à l\'API:', submissionData);
        const response = await ApiService.post('/reponses', submissionData);
        console.log('Réponse API enregistrement:', response);
        if (response.data && (response.data.code === 201 || response.data.code === 200)) {
          console.log(`✓ Réponse enregistrée avec succès pour la question ${questionId}`);
          return true;
        } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'enregistrement');
        }
      } catch (err: any) {
        console.error('Erreur lors de l\'enregistrement de la réponse:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Erreur inconnue';
        error(`Erreur lors de l'enregistrement: ${errorMessage}`);
        return false;
      }
    };

    const previousQuestion = () => {
      if (currentQuestionIndex.value > 0) {
        if (currentQuestion.value && formData.answer) {
          answers.value[currentQuestion.value.id] = formData.answer;
        }
        currentQuestionIndex.value--;
        const savedAnswer = answers.value[currentQuestion.value?.id || 0];
        formData.answer = savedAnswer || null;
        if (testForm.value) {
          testForm.value.resetForm({ values: { answer: formData.answer } });
        }
        onAnswerChange();
      }
    };

    const handleSubmit = async (values: FormValues) => {
      console.log('=== SOUMISSION ===');
      console.log('Valeurs soumises:', values);
      console.log('Question actuelle:', currentQuestion.value);
     
      if (!currentQuestion.value) {
        error('Aucune question disponible.');
        return;
      }
      try {
        isSubmitting.value = true;
        const saveSuccess = await saveAnswer(currentQuestion.value.id, values.answer || '');
        if (!saveSuccess) {
          error('Impossible d\'enregistrer votre réponse. Veuillez réessayer.');
          return;
        }
        answers.value[currentQuestion.value.id] = values.answer || '';
        console.log('Réponses sauvegardées:', answers.value);
        if (isLastQuestion.value) {
          // Calculer les résultats localement avant d'enregistrer
          let correctAnswers = 0;
          questions.value.forEach((question) => {
            const userAnswer = answers.value[question.id];
            if (userAnswer) {
              const correctProposition = question.propositions?.find(p => p.estLaBonneReponse);
              if (correctProposition) {
                if (question.type === 'choix_multiple' || question.type === 'oui_non') {
                  if (String(correctProposition.id) === userAnswer) {
                    correctAnswers++;
                  }
                } else if (question.type === 'texte_libre') {
                  if (userAnswer.toLowerCase().trim() === correctProposition.contenu.toLowerCase().trim()) {
                    correctAnswers++;
                  }
                }
              }
            }
          });
          testResults.score = correctAnswers;
          testResults.total = questions.value.length;
          testResults.percentage = Math.round((correctAnswers / questions.value.length) * 100);
          testResults.correctAnswers = correctAnswers;
          testResults.incorrectAnswers = questions.value.length - correctAnswers;
          // Enregistrer les résultats via API
          const resultsSaved = await saveTestResults();
          if (!resultsSaved) {
            error('Impossible d\'enregistrer les résultats. Veuillez réessayer.');
            return;
          }
          // Récupérer les résultats pour confirmation
          await fetchTestResults();
         
          success('Test terminé avec succès ! Toutes vos réponses et résultats ont été enregistrés.');
         
          // Afficher les résultats
          showResults.value = true;
        } else {
          currentQuestionIndex.value++;
          const nextQuestion = currentQuestion.value;
          const savedAnswer = answers.value[nextQuestion?.id || 0];
          formData.answer = savedAnswer || null;
          if (testForm.value) {
            testForm.value.resetForm({ values: { answer: formData.answer } });
          }
          onAnswerChange();
        }
      } catch (err: any) {
        console.error('Erreur lors de la soumission:', err);
        error(err.response?.data?.message || 'Une erreur est survenue.');
      } finally {
        isSubmitting.value = false;
      }
    };

    watch(
      () => route.params.id,
      () => {
        loadSessionQuestions();
      }
    );

    onMounted(() => {
      console.log('=== DÉBUT CHARGEMENT DES QUESTIONS ===');
      console.log('Paramètres de route:', route.params);
      loadSessionQuestions();
    });

    return {
      testForm,
      questions,
      currentQuestionIndex,
      currentQuestion,
      formData,
      testSchema,
      propositionOptions,
      isLastQuestion,
      isDisable,
      isLoading,
      isSubmitting,
      progressPercentage,
      answeredCount,
      sessionId,
      sessionCode, // Retourner sessionCode
      showResults,
      testResults,
      questionsWithAnswers,
      isTestCompleted,
      previousQuestion,
      handleSubmit,
      onAnswerChange,
    };
  },
});
</script>

<style scoped>
.card {
  border-radius: 0;
  background-color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.results-container {
  background-color: transparent !important;
}
.form-control,
.form-check-input {
  border-radius: 0;
  border-color: #ced4da;
}
.text-black {
  color: #212529;
}
.text-danger {
  color: #dc3545;
  font-size: 0.875rem;
  font-weight: 500;
}
.text-success {
  color: #198754;
  font-weight: 500;
}
.border-primary {
  border-color: #0d6efd !important;
  border-width: 2px;
}
.btn-success {
  background-color: #198754;
  border-color: #198754;
}
.btn-info {
  background-color: #0dcaf0;
  border-color: #0dcaf0;
}
.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}
.btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
}
.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}
.btn:hover {
  filter: brightness(90%);
  transition: all 0.2s ease;
}
.cursor-not-allowed {
  cursor: not-allowed;
  opacity: 0.7;
}
.p-5 {
  padding: 5px;
}
.p-10 {
  padding: 10px;
}
.p-15 {
  padding: 15px;
}
.mb-5 {
  margin-bottom: 5px;
}
.mb-10 {
  margin-bottom: 10px;
}
.mb-15 {
  margin-bottom: 15px;
}
.mb-20 {
  margin-bottom: 20px;
}
.mt-10 {
  margin-top: 10px;
}
.mt-20 {
  margin-top: 20px;
}
.fs-4 {
  font-size: 1.5rem;
  font-weight: 600;
}
.progress {
  background-color: #e9ecef;
  border-radius: 0;
}
.progress-bar {
  background-color: #198754;
  transition: width 0.3s ease;
  font-weight: 500;
}
.progress-bar.bg-danger {
  background-color: #dc3545 !important;
}
.alert {
  border-radius: 0;
  border: 1px solid;
}
.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}
.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}
.alert-warning {
  background-color: #fff3cd;
  border-color: #ffc107;
  color: #856404;
}
.spinner-border {
  width: 2rem;
  height: 2rem;
  border-color: #0d6efd;
  border-right-color: transparent;
}
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
.questions-review {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 10px;
}
.question-review-item {
  transition: all 0.3s ease;
  border-width: 2px;
  border-radius: 4px;
}
.question-review-item.border-success {
  border-color: #198754;
}
.question-review-item.border-danger {
  border-color: #dc3545;
}
.question-review-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}
.question-header {
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
}
.question-number {
  font-size: 1rem;
}
.bg-success-subtle {
  background-color: #e6f4ea !important;
}
.bg-danger-subtle {
  background-color: #f8d7da !important;
}
.text-decoration-line-through {
  text-decoration: line-through;
  text-decoration-color: #dc3545;
  text-decoration-thickness: 2px;
  color: #dc3545 !important;
  font-weight: 600;
}
.user-answer {
  border-left: 4px solid #6c757d;
  background-color: #f8f9fa;
  border-radius: 4px;
}
.user-answer .text-success {
  color: #198754 !important;
}
.correct-answer-display {
  border-left: 4px solid #198754;
  background-color: #e6f4ea;
  border-radius: 4px;
}
.badge.bg-success {
  background-color: #198754 !important;
  color: #ffffff;
}
.badge.bg-danger {
  background-color: #dc3545 !important;
  color: #ffffff;
}
.badge.bg-secondary {
  background-color: #6c757d !important;
  color: #ffffff;
}
</style>